/**
 * ABSCHLUSSSCHRITT DER MODULE — reine Regeln (Plan Paketposition, Stufe B5).
 *
 * Nach Freigabe bzw. Versand zeigt jedes der sechs Dokumentmodule
 * (Vorankuendigung, UfsA, SiGe-Plan, Baustellenordnung/HSE-Plan, A+S-Plan,
 * Handbuch) einen Abschlussschritt „Leistung buchen". WAS er anbietet, haengt
 * am LV des Projekts:
 *
 *   Fall 1 'position' — eine EIGENE LV-Position fuer die Unterlage: die
 *                       Positionsauswahl wie bisher (BillingSelectionModal bzw.
 *                       die Abrechnungszeile der Baustellenordnung).
 *   Fall 2 'paket'    — nur eine Paketposition mit einem Bestandteil dieser
 *                       Unterlage: KEIN Auswahldialog. Den Bestandteil
 *                       erledigt die Datenbank per Trigger auf der Ausgabe
 *                       (SQL Nr. 166); der Schritt meldet das und bucht die
 *                       Paketposition, wenn sie damit vollstaendig ist
 *                       (bestandteilBuchung.nachAusgabeBestandteile).
 *   Fall 3 'beides'   — beides; der Projektcheck warnt gelb (E9).
 *   'keiner'          — weder noch: kein Schritt.
 *
 * Nur-Ansicht, Freelancer und ein Modul ohne Schreibweg (`onUpdate`) bekommen
 * keinen Schritt, nur einen Satz (`abschlussSperre`).
 *
 * Hier steht nur, was ohne React und ohne Netz pruefbar ist: welcher Fall,
 * welche Saetze, wie mehrere Ausgaben einer Freigabe (Baustellenordnung UND
 * HSE-Plan) zu EINER Meldung werden, und die Regeln des Stunden-
 * Pflichtfensters (Stundenkontingent S3). Der Hook (`useBestandteilAbschluss`)
 * fuehrt aus, die Anzeige (`BestandteilAbschlussHinweis`,
 * `BestandteilStundenPflicht`) zeigt an.
 *
 * KEINE PREISE: nichts hier liest `unitPrice` — die Saetze nennen Titel,
 * LV-Nummer und Mengen, nie Geld (Kernregel 6, Merkregel „Preise nur fuer
 * den Innendienst").
 */

import type { BestandteilQuelle, ServiceItem } from '../../types';
import type { AusgabeFolge } from '../../services/bestandteile/bestandteilBuchung';
import { BESTANDTEIL_QUELLE_LABEL, hatBestandteile } from '../../services/bestandteile/bestandteilLogic';
import { STUNDEN_RPC_STATUS_TEXT, type StundenRpcErgebnis } from '../../services/bestandteile/bestandteilService';
import { formatStunden, parseStunden } from '../../services/bestandteile/stundenLogic';
import { buildActorName } from '../../services/actorName';
import { isHourlyUnit } from '../../services/serviceUnits';

// ─────────────────────────────────────────────────────────────────────────────
// Welcher Fall?
// ─────────────────────────────────────────────────────────────────────────────

export type AbschlussFall = 'keiner' | 'position' | 'paket' | 'beides';

/**
 * Die Paketpositionen des Stands, die einen Bestandteil DIESER Quelle tragen.
 * Gelesen aus dem Stand der Sitzung — die Ausfuehrung liest danach frisch aus
 * der Datenbank; hier geht es nur um die Frage „gibt es einen Schritt?".
 */
export const paketeFuerQuelle = (
    services: readonly ServiceItem[] | null | undefined,
    quelle: BestandteilQuelle,
): ServiceItem[] =>
    (services || []).filter(s =>
        !!s && hatBestandteile(s) && (s.bestandteile || []).some(b => !!b && b.quelle === quelle));

/**
 * Der Fall aus den angebotenen eigenen Positionen und den Paketen.
 * `positionen` ist die Liste, die der Positionsauswahl gegeben wuerde
 * (selectBillableServices u. a.) — eine Paketposition darin zaehlt NICHT als
 * eigene Position (sie ist nie ein direktes Buchungsziel, Kernregel 7).
 */
export const bestimmeAbschlussFall = (a: {
    positionen: readonly ServiceItem[] | null | undefined;
    pakete: readonly ServiceItem[] | null | undefined;
}): AbschlussFall => {
    const position = (a.positionen || []).some(s => !!s && !hatBestandteile(s));
    const paket = (a.pakete || []).length > 0;
    if (position && paket) return 'beides';
    if (position) return 'position';
    if (paket) return 'paket';
    return 'keiner';
};

/** Fall 1 und 3: die Positionsauswahl wird angeboten. */
export const mitPositionsauswahl = (fall: AbschlussFall): boolean => fall === 'position' || fall === 'beides';

/** Fall 2 und 3: nach der Ausgabe wird nach den Bestandteilen gefragt. */
export const mitBestandteilen = (fall: AbschlussFall): boolean => fall === 'paket' || fall === 'beides';

// ─────────────────────────────────────────────────────────────────────────────
// Wer bekommt keinen Schritt?
// ─────────────────────────────────────────────────────────────────────────────

export type AbschlussSperre = 'nur_ansicht' | 'freelancer' | 'kein_schreibweg';

/**
 * Kein Schritt in der Nur-Ansicht (geschrieben wuerde nichts), fuer
 * Freelancer (kein Buchungsrecht; sie sehen die Module ohnehin nur im
 * Portal — das ist die zweite Reihe) und ohne Schreibweg: ohne `onUpdate`
 * kaeme eine Buchung nie im Projekt an. `null` = Schritt erlaubt.
 */
export const abschlussSperre = (a: {
    isReadOnly?: boolean | null;
    rolle?: string | null;
    kannSchreiben: boolean;
}): AbschlussSperre | null => {
    if (a.isReadOnly) return 'nur_ansicht';
    if ((a.rolle || '').trim().toLowerCase() === 'freelancer') return 'freelancer';
    if (!a.kannSchreiben) return 'kein_schreibweg';
    return null;
};

/** Der Satz statt des Schritts — die Paketposition bucht dann der Nachtlauf. */
export const SPERRE_TEXT: Record<AbschlussSperre, string> = {
    nur_ansicht: 'Nur-Ansicht: hier wird keine Leistung gebucht. Ist die Paketposition vollständig, '
        + 'bucht sie der Nachtlauf.',
    freelancer: 'Als Freelancer wird hier keine Leistung gebucht. Ist die Paketposition vollständig, '
        + 'bucht sie der Nachtlauf.',
    kein_schreibweg: 'Dem Modul wurde der Leistungsstand des Projekts nicht übergeben — hier wird nichts gebucht. '
        + 'Ist die Paketposition vollständig, bucht sie der Nachtlauf.',
};

// ─────────────────────────────────────────────────────────────────────────────
// „Vorgang ohne Leistungsbuchung" bei wartendem Paket (Betreiber, Restrisiko 4)
// ─────────────────────────────────────────────────────────────────────────────

/** Die Module, deren Export im Auswahldialog „ohne Leistungsbuchung" kennt. */
export type ExportModul = 'vorankuendigung' | 'unterlage' | 'sigeplan';

/**
 * Was ein Export dieses Moduls OHNE Leistungsbuchung ablegt:
 *  - 'ausgabe': er legt trotzdem eine Ausgabe ab, die den Bestandteil
 *    erledigen kann — Vorankuendigung (jeder Export ist ein VERSAND, E3) und
 *    die finale Unterlage (Ablage als Freigabe, ufsaAblageAnlassFuerExport);
 *  - 'kein_freigabestand': ein Entwurf der Unterlage — er legt nichts als
 *    Ausgabe ab, ist aber auch nicht der fertige Stand; zu fragen gibt es nichts;
 *  - 'keine_ausgabe': der SiGe-Plan — ohne Buchung ist der Export ein
 *    Zwischenstand und legt nichts ab. Wartet ein Paket mit dem Bestandteil
 *    „SiGe-Plan", bliebe er offen, obwohl der Plan fertig ist (die Falle).
 */
export const exportOhneBuchung = (
    modul: ExportModul,
    a: { isDraft?: boolean } = {},
): 'ausgabe' | 'kein_freigabestand' | 'keine_ausgabe' => {
    if (modul === 'vorankuendigung') return 'ausgabe';
    if (modul === 'unterlage') return a.isDraft ? 'kein_freigabestand' : 'ausgabe';
    return 'keine_ausgabe';
};

/**
 * Was „Vorgang ohne Leistungsbuchung" (bzw. Fall 2 ohne eigene Position)
 * ausloest:
 *  - 'fassung_fragen': der Export legte nichts ab, aber ein Paket mit diesem
 *    Bestandteil wartet — dann dieselbe Frage wie in Fall 2: „Als Fassung
 *    ausgeben" (legt als Versand ab, der Trigger erledigt den Bestandteil, die
 *    eigene Position wird NICHT gebucht) oder „Nur herunterladen"
 *    (Zwischenstand);
 *  - 'export': sonst wie gewaehlt — ohne Paket, bei einer Sperre (Nur-Ansicht,
 *    Freelancer, ohne Schreibweg) oder wenn der Export ohnehin ablegt.
 * Rein.
 */
export const schrittOhneLeistungsbuchung = (a: {
    modul: ExportModul;
    isDraft?: boolean;
    pakete: readonly ServiceItem[] | null | undefined;
    sperre: AbschlussSperre | null;
}): 'export' | 'fassung_fragen' =>
    exportOhneBuchung(a.modul, { isDraft: a.isDraft }) === 'keine_ausgabe' && (a.pakete || []).length > 0 && !a.sperre
        ? 'fassung_fragen'
        : 'export';

// ─────────────────────────────────────────────────────────────────────────────
// Neue Fassung: „schon gebucht" vorher sagen
// ─────────────────────────────────────────────────────────────────────────────

const zahl = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const mengeText = (n: number): string => n.toLocaleString('de-DE', { maximumFractionDigits: 3 });

const positionName = (s: Pick<ServiceItem, 'lvPosition' | 'title'>): string =>
    `${(s.lvPosition || '').trim()} ${(s.title || '').trim()}`.trim() || 'ohne Titel';

/** Eigene Positionen, auf die schon gebucht ist — dieselbe Frage wie „Bearbeitung oder Neuerstellung". */
export const schonGebuchtePositionen = (positionen: readonly ServiceItem[] | null | undefined): ServiceItem[] =>
    (positionen || []).filter(s => !!s && !hatBestandteile(s) && zahl(s.quantityUsed) > 0);

/**
 * Der Satz VOR der Positionsauswahl, wenn eine angebotene eigene Position
 * schon gebucht ist (Plan B5 „Neue Fassung"): die Freigabe einer neuen
 * Fassung bietet die Buchung erneut an, sagt aber vorher, dass die Pauschale
 * schon steht — sonst buchte der zweite Klick still ein zweites Mal. Muster:
 * der Dialog „Bestehende bearbeiten / Neu erstellen" der Vorankuendigung und
 * der Unterlage. `null`, wenn nichts gebucht ist.
 */
export const schonGebuchtHinweis = (positionen: readonly ServiceItem[] | null | undefined): string | null => {
    const gebucht = schonGebuchtePositionen(positionen);
    if (gebucht.length === 0) return null;
    const teile = gebucht.map(s => {
        const soll = zahl(s.quantityContracted);
        const einheit = (s.unit || '').trim();
        const stand = soll > 0
            ? `${mengeText(zahl(s.quantityUsed))} von ${mengeText(soll)}${einheit ? ` ${einheit}` : ''}`
            : `${mengeText(zahl(s.quantityUsed))}${einheit ? ` ${einheit}` : ''}`;
        return `„${positionName(s)}“ (${stand})`;
    });
    const wer = gebucht.length === 1 ? `Die Position ${teile[0]} ist` : `Die Positionen ${teile.join(', ')} sind`;
    return `${wer} bereits gebucht. Eine neue Fassung ist nur dann eine weitere Leistung, wenn sie beauftragt ist — `
        + 'sonst „Vorgang ohne Leistungsbuchung abschließen“.';
};

/**
 * Voll gebuchte PAUSCHALEN einer Auswahl aus allen Positionen (Handbuch, Plan
 * B5): Soll erreicht, keine Stundenposition, Menge 1 oder Einheit „Pauschal".
 * Teilweise gebuchte Kontingente (Begehungen, Stunden) gehoeren nicht in den
 * Satz „bereits gebucht" — dort waere er Rauschen.
 */
export const vollGebuchtePauschalen = (positionen: readonly ServiceItem[] | null | undefined): ServiceItem[] =>
    (positionen || []).filter(s => {
        if (!s || hatBestandteile(s) || isHourlyUnit(s.unit)) return false;
        const soll = zahl(s.quantityContracted);
        if (!(soll > 0) || zahl(s.quantityUsed) < soll - 0.0005) return false;
        return soll === 1 || /pausch|psch/i.test(s.unit || '');
    });

// ─────────────────────────────────────────────────────────────────────────────
// Ausgaben einer Freigabe → eine Meldung
// ─────────────────────────────────────────────────────────────────────────────

export const leereFolge = (): AusgabeFolge => ({ erledigt: [], gebucht: [], hinweise: [] });

/** Die Kennungen der Ausgaben, bereinigt: leer, doppelt und Nicht-Text fallen weg. */
export const bereinigeAusgabeIds = (
    ids: ReadonlyArray<string | null | undefined> | string | null | undefined,
): string[] => {
    const liste = Array.isArray(ids) ? ids : [ids];
    const out: string[] = [];
    for (const roh of liste) {
        const id = typeof roh === 'string' ? roh.trim() : '';
        if (id && !out.some(x => x.toLowerCase() === id.toLowerCase())) out.push(id);
    }
    return out;
};

/**
 * Mehrere Folgen (eine je Ausgabe — Baustellenordnung und HSE-Plan einer
 * Freigabe sind zwei Ausgaben derselben Unterlage) werden EINE Meldung:
 * erledigt je Eintrag einmal (der spaetere Stand gilt), gebucht je Position
 * einmal, Saetze einmal. Ein „war bereits gebucht" zu einer Position, die
 * DIESER Ablauf gebucht hat, faellt weg — er ist ein Echo der eigenen Buchung.
 *
 * „Je Eintrag": ueber `eintragId`, wenn bekannt — zwei Bestandteile
 * derselben Position mit gleichem Namen sind zwei Eintraege und bleiben
 * beide (jeder braucht seine Stunden). Ohne Kennung wie bisher je Position +
 * Bestandteilname.
 */
const erledigtSchluessel = (e: AusgabeFolge['erledigt'][number]): string => {
    const id = typeof e.eintragId === 'string' ? e.eintragId.trim().toLowerCase() : '';
    return id ? `eintrag\u0000${id}` : `${e.serviceId}\u0000${e.bestandteilLabel}`;
};

export const fuehreFolgenZusammen = (folgen: ReadonlyArray<AusgabeFolge | null | undefined>): AusgabeFolge => {
    const erledigt = new Map<string, AusgabeFolge['erledigt'][number]>();
    const gebucht = new Map<string, AusgabeFolge['gebucht'][number]>();
    const hinweise: string[] = [];
    for (const f of folgen) {
        if (!f) continue;
        for (const e of f.erledigt || []) erledigt.set(erledigtSchluessel(e), e);
        for (const g of f.gebucht || []) if (!gebucht.has(g.serviceId)) gebucht.set(g.serviceId, g);
        for (const h of f.hinweise || []) {
            const t = (h || '').trim();
            if (t && !hinweise.includes(t)) hinweise.push(t);
        }
    }
    const echo = new Set([...gebucht.values()].map(g => `Position „${g.positionTitel}“ war bereits gebucht.`));
    return {
        erledigt: [...erledigt.values()],
        gebucht: [...gebucht.values()],
        hinweise: hinweise.filter(h => !echo.has(h)),
    };
};

/**
 * Fragt je Ausgabe nach (`einzel`, in der Ausfuehrung
 * `nachAusgabeBestandteile` mit genau dieser `ausgabeId`) — NACHEINANDER,
 * damit die zweite Buchung auf dem Stand der ersten aufsetzt — und fuehrt die
 * Folgen zusammen. `weiter` bricht zwischen zwei Ausgaben ab (Modul
 * ausgehaengt). Wirft nie: ein Fehler wird ein Satz.
 */
export const fuehreAusgabenAus = async (
    ids: readonly string[],
    einzel: (ausgabeId: string) => Promise<AusgabeFolge>,
    weiter: () => boolean = () => true,
): Promise<AusgabeFolge> => {
    const folgen: AusgabeFolge[] = [];
    for (const id of ids) {
        if (!weiter()) break;
        try {
            folgen.push(await einzel(id));
        } catch (e) {
            folgen.push({ ...leereFolge(), hinweise: [`Bestandteile: ${e instanceof Error ? e.message : String(e)}`] });
        }
    }
    return fuehreFolgenZusammen(folgen);
};

/**
 * Keine Ausgabe festgehalten (Steckbrief nicht lesbar, Journal gescheitert):
 * dann hat auch der Trigger nichts erledigt. Der Satz sagt das je
 * Bestandteil dieser Quelle — ob er schon frueher erledigt war, weiss die
 * Sitzung nicht, deshalb „ist er noch offen".
 */
export const ohneAusgabeHinweise = (
    pakete: readonly ServiceItem[] | null | undefined,
    quelle: BestandteilQuelle,
): string[] => {
    const saetze: string[] = [];
    for (const p of pakete || []) {
        for (const b of p?.bestandteile || []) {
            if (!b || b.quelle !== quelle) continue;
            saetze.push(
                `Bestandteil „${b.label}“ der Position „${(p.title || '').trim() || 'ohne Titel'}“: die Ausgabe wurde nicht `
                + 'festgehalten, deshalb hat sie ihn nicht erledigt. Ist er noch offen, in der Liste der Bestandteile buchen.',
            );
        }
    }
    return saetze;
};

/**
 * Die Fassung lag schon frueher aus (`AblageErgebnis.vorhandeneAusgabeId`):
 * diese Handlung hat keine Ausgabe geschrieben und damit auch nichts
 * erledigt. Der Schritt MELDET nur — gebucht wird nichts, denn sonst stuende
 * eine unbeteiligte Handlung als Ausloeser da (Kernregel 7).
 */
export const schonAusgegebenHinweise = (
    pakete: readonly ServiceItem[] | null | undefined,
    quelle: BestandteilQuelle,
): string[] => {
    const saetze: string[] = [];
    for (const p of pakete || []) {
        for (const b of p?.bestandteile || []) {
            if (!b || b.quelle !== quelle) continue;
            saetze.push(
                `Bestandteil „${b.label}“ der Position „${(p.title || '').trim() || 'ohne Titel'}“: diese Fassung lag schon `
                + 'früher als Ausgabe vor — hier wird nichts erneut erledigt oder gebucht. Der Stand steht in der Liste der Bestandteile.',
            );
        }
    }
    return saetze;
};

/**
 * Was der Abschlussschritt mit dem Ergebnis einer Ablage bzw. eines
 * Freigabe-Journals tut:
 *  - 'nachfragen' mit den Kennungen, die DIESER Vorgang geschrieben hat
 *    (`ausgabeId` der Ablage, `ausgabeIds` des Journals);
 *  - 'melden', wenn nur aeltere Ausgaben der Fassung vorliegen
 *    (`vorhandeneAusgabeId(s)`) — nie Ausloeser einer Buchung;
 *  - 'ohne_ausgabe', wenn keine entstand (Hinweis „nicht erledigt").
 */
export const schrittNachAblage = (e: {
    ausgabeId?: string | null;
    vorhandeneAusgabeId?: string | null;
    ausgabeIds?: ReadonlyArray<string | null | undefined> | null;
    vorhandeneAusgabeIds?: ReadonlyArray<string | null | undefined> | null;
} | null | undefined): { art: 'nachfragen'; ausgabeIds: string[] } | { art: 'melden' } | { art: 'ohne_ausgabe' } => {
    const eigene = bereinigeAusgabeIds([e?.ausgabeId, ...(e?.ausgabeIds ?? [])]);
    if (eigene.length > 0) return { art: 'nachfragen', ausgabeIds: eigene };
    if (bereinigeAusgabeIds([e?.vorhandeneAusgabeId, ...(e?.vorhandeneAusgabeIds ?? [])]).length > 0) return { art: 'melden' };
    return { art: 'ohne_ausgabe' };
};

// ─────────────────────────────────────────────────────────────────────────────
// Zweite Sperre: Paketpositionen sind nie direktes Buchungsziel
// ─────────────────────────────────────────────────────────────────────────────

/** Die Kennungen der Paketpositionen eines Stands (Kernregel 7). */
export const paketPositionIds = (services: readonly ServiceItem[] | null | undefined): Set<string> =>
    new Set((services || []).filter(s => !!s?.id && hatBestandteile(s)).map(s => s.id));

/**
 * Laesst Protokollzeilen weg, die eine Paketposition direkt buchen wuerden —
 * die zweite Sperre hinter jeder Auswahl (etwa die Handzeile im
 * Leistungsnachweis-Editor). Ein Paket bucht nur der Abschlussschritt bzw.
 * der Dialog der Bestandteile (`BST-…`), sonst der Nachtlauf.
 */
export const ohnePaketZeilen = <T extends { serviceId?: string | null }>(
    zeilen: readonly T[],
    services: readonly ServiceItem[] | null | undefined,
): T[] => {
    const pakete = paketPositionIds(services);
    return zeilen.filter(z => !pakete.has(String(z?.serviceId ?? '')));
};

/** Der Satz, wenn ein Modul eine Paketposition als Ziel bekam. */
export const PAKET_NICHT_DIREKT =
    'Die gewählte Position ist eine Paketposition mit Bestandteilen — sie wird nicht von hier gebucht, '
    + 'sondern sobald alle Bestandteile erledigt sind.';

// ─────────────────────────────────────────────────────────────────────────────
// Stunden-Pflichtfenster nach der Ausgabe (Stundenkontingent S3)
// ─────────────────────────────────────────────────────────────────────────────
//
// Erledigt eine Ausgabe Bestandteile einer Paketposition (Trigger aus Nr. 166,
// `durch = 'modul'`, `stunden` NULL), fragt der Abschlussschritt direkt danach
// die Stunden dafuer ab — PFLICHT (Betreiber: „Stunden eintragen ist
// Pflicht"): kein Ueberspringen, kein Schliessen, kein ESC, kein Klick
// daneben. Erst wenn ein Speichern gescheitert ist (Antwort der Datenbank oder
// Netz), darf das Fenster mit „Später nachtragen" zu; die Meldung zeigt dann
// gelb „Stunden fehlen", nachgetragen wird im Dialog der Bestandteile (S7).
//
// Je Eintrag fragt ein Modul hoechstens EINMAL (`bereitsGefragt`, gefuehrt im
// Hook): meldet ein zweiter Lauf dieselbe Erledigung (Erneut festhalten,
// Zeitfenster ohne Ausgabe-Kennung), gibt es nur den gelben Satz.
//
// Stunden sind intern (S8): keine Preise, nichts davon auf Rechnung.

export type StundenZeilenStatus =
    /** wartet auf die Eingabe */
    | 'offen'
    /** gespeichert (`gesetzt`/`unveraendert`) */
    | 'gespeichert'
    /** inzwischen hat jemand anderes Stunden eingetragen — gilt als erledigt, nichts ueberschrieben */
    | 'bereits'
    /** Speichern gescheitert — erneut versuchen oder „Später nachtragen" */
    | 'fehler'
    /** Eintrag zurueckgenommen bzw. nicht mehr da — hier gibt es nichts mehr einzutragen */
    | 'hinfaellig';

export interface StundenOhneFenster {
    serviceId: string;
    positionTitel: string;
    bestandteilLabel: string;
    /**
     * Der Eintrag, wenn bekannt — damit ein Eintrag, der nur wegen des
     * Einmal-Schutzes hier landet, aber noch im OFFENEN Fenster steht, keinen
     * gelben Satz bekommt (`stundenAbfragenZusammen`).
     */
    eintragId?: string | null;
}

export interface StundenPflichtZeile extends StundenOhneFenster {
    eintragId: string;
    status: StundenZeilenStatus;
    /** gespeichert/bereits: die Stunden (laut Datenbank, sonst die eingegebenen); sonst null. */
    stunden: number | null;
    /** fehler/hinfaellig/bereits: der Klartext dazu. */
    meldung: string | null;
}

export interface StundenAbfrage {
    /** Kennung dieser Abfrage — eine Antwort zu einer aelteren Abfrage verfaellt. */
    id: number;
    /** Die Zeilen des Fensters: je erledigtem Bestandteil ohne Stunden einer. */
    zeilen: StundenPflichtZeile[];
    /**
     * Erledigt ohne Stunden, aber OHNE Fenster: Eintrag nicht ermittelbar,
     * schon einmal gefragt, oder der Vorgang gehoert nicht mehr zum offenen
     * Projekt. Nur der gelbe Satz.
     */
    ohneFenster: StundenOhneFenster[];
    /** Mindestens ein Speichern ist gescheitert — erst dann darf das Fenster ohne Stunden zu (S3). */
    fehlversuch: boolean;
    /** „Später nachtragen" gewaehlt. */
    spaeter: boolean;
    /** Speichern laeuft. */
    speichert: boolean;
}

/** Der gelbe Satz (S3). */
export const STUNDEN_FEHLEN_TEXT = 'Stunden fehlen — im Bestandteile-Dialog nachtragen';

/** Kollege war schneller (RPC verlangt dann einen Grund bzw. weist ab, S7). */
export const STUNDEN_BEREITS_TEXT = 'Bereits eingetragen — inzwischen hat jemand die Stunden erfasst; nichts überschrieben.';

/** Feldfehler im Fenster (S9, wie `parseStunden`). */
export const STUNDEN_EINGABE_UNGUELTIG =
    'Bitte eine Stundenzahl über 0 bis 9999 eingeben, höchstens zwei Nachkommastellen (z. B. 7,5).';

const textOder = (v: unknown, ersatz: string): string => (typeof v === 'string' && v.trim() ? v.trim() : ersatz);

/** Vergleichsschluessel einer Eintrags-id (uuid — Gross/Klein egal). */
export const stundenEintragSchluessel = (id: string): string => String(id ?? '').trim().toLowerCase();

/** Stunden als endliche Zahl, sonst null. */
const stundenZahl = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * Aus der Folge einer Ausgabe: welche erledigten Bestandteile ins Fenster
 * kommen (Stunden fehlen, Eintrag bekannt, noch nie gefragt, Fenster erlaubt)
 * und welche nur den gelben Satz bekommen. `null`: es fehlen keine Stunden.
 *
 * `fensterErlaubt` false: Nur-Ansicht, Freelancer, oder der Vorgang gehoert
 * nicht mehr zum offenen Projekt — dann nie ein Fenster.
 *
 * `stundenUnbekannt` (die Datenbank kennt die Stunden noch nicht, Nr. 171/172
 * fehlen): weder Fenster noch gelber Satz — das Speichern scheiterte nur an
 * `rpc_fehlt`, und der Dialog blendet die Stunden in diesem Zustand aus.
 */
export const stundenAbfrageAus = (
    folge: AusgabeFolge | null | undefined,
    a: { id: number; fensterErlaubt: boolean; bereitsGefragt?: ReadonlySet<string> | null },
): StundenAbfrage | null => {
    const zeilen: StundenPflichtZeile[] = [];
    const ohneFenster: StundenOhneFenster[] = [];
    const gesehen = new Set<string>();
    for (const e of folge?.erledigt || []) {
        if (!e || e.stundenUnbekannt === true || stundenZahl(e.stunden) !== null) continue;
        const kopf: StundenOhneFenster = {
            serviceId: String(e.serviceId ?? ''),
            positionTitel: textOder(e.positionTitel, 'ohne Titel'),
            bestandteilLabel: textOder(e.bestandteilLabel, 'ohne Namen'),
        };
        const id = typeof e.eintragId === 'string' ? e.eintragId.trim() : '';
        const schluessel = stundenEintragSchluessel(id);
        if (id) {
            if (gesehen.has(schluessel)) continue;
            gesehen.add(schluessel);
        }
        if (!id || !a.fensterErlaubt || a.bereitsGefragt?.has(schluessel)) {
            ohneFenster.push(id ? { ...kopf, eintragId: id } : kopf);
            continue;
        }
        zeilen.push({ ...kopf, eintragId: id, status: 'offen', stunden: null, meldung: null });
    }
    if (zeilen.length === 0 && ohneFenster.length === 0) return null;
    return { id: a.id, zeilen, ohneFenster, fehlversuch: false, spaeter: false, speichert: false };
};

/** Zeilen, in die noch etwas einzutragen ist. */
const brauchtEingabe = (z: StundenPflichtZeile): boolean => z.status === 'offen' || z.status === 'fehler';

/** Steht das Fenster offen? Solange eine Zeile Stunden braucht und nicht „Später nachtragen" gewaehlt ist. */
export const stundenFensterOffen = (a: StundenAbfrage | null | undefined): boolean =>
    !!a && !a.spaeter && a.zeilen.some(brauchtEingabe);

/**
 * Antwort von `setzeBestandteilStunden` → Zustand der Zeile.
 *  - `gesetzt`/`unveraendert` → gespeichert;
 *  - `grund_zu_kurz` / `nur_admin` (42501): die Stunden stehen inzwischen —
 *    ein Kollege war schneller (die RPC verlangt dann einen Grund bzw. weist
 *    ab). Gilt als erledigt: „bereits eingetragen", nichts ueberschrieben;
 *  - `zurueckgenommen`/`eintrag_fehlt`/`position_fehlt` → hinfaellig, Klartext;
 *  - alles andere (Netz, Rechte, Riegel, `stunden_ungueltig` …) → Fehler mit
 *    dem Klartext des Dienstes.
 */
export const stundenErgebnisDeuten = (
    r: StundenRpcErgebnis | null | undefined,
    eingegeben: number,
): Pick<StundenPflichtZeile, 'status' | 'stunden' | 'meldung'> => {
    const status = r?.status;
    if (status === 'gesetzt' || status === 'unveraendert') {
        return { status: 'gespeichert', stunden: stundenZahl(r?.stunden) ?? eingegeben, meldung: null };
    }
    if (status === 'grund_zu_kurz' || status === 'nur_admin') {
        return { status: 'bereits', stunden: stundenZahl(r?.stunden), meldung: STUNDEN_BEREITS_TEXT };
    }
    if (status === 'zurueckgenommen' || status === 'eintrag_fehlt' || status === 'position_fehlt') {
        return { status: 'hinfaellig', stunden: null, meldung: STUNDEN_RPC_STATUS_TEXT[status] };
    }
    const text = status && Object.prototype.hasOwnProperty.call(STUNDEN_RPC_STATUS_TEXT, status)
        ? STUNDEN_RPC_STATUS_TEXT[status]
        : STUNDEN_RPC_STATUS_TEXT.fehler;
    const detail = (!status || status === 'fehler') && r?.fehler ? ` (${r.fehler})` : '';
    return { status: 'fehler', stunden: null, meldung: `${text}${detail}` };
};

/**
 * Zeitlimit je Aufruf. Der Supabase-Client kennt keinen fetch-Timeout — ohne
 * Limit stuende das Pflichtfenster bei haengendem Netz ohne Ausweg da
 * („Später nachtragen" gibt es erst nach einem Fehlversuch).
 */
export const STUNDEN_SPEICHERN_ZEITLIMIT_MS = 20_000;

export const STUNDEN_ZEITLIMIT_TEXT =
    'Keine Antwort der Datenbank (Zeitüberschreitung) — ob die Stunden angekommen sind, ist offen. '
    + 'Erneut versuchen oder „Später nachtragen“.';

export const STUNDEN_NICHT_GESENDET_TEXT =
    'Nicht gesendet — die Verbindung hing schon beim Bestandteil davor. Erneut versuchen oder „Später nachtragen“.';

/**
 * `p` mit Zeitlimit: die Antwort, oder nach `ms` „zeitlimit". Eine SPAETER
 * eintreffende Antwort (oder ein spaeter Wurf) verpufft — das Versprechen ist
 * dann schon erfuellt, und beide Zweige sind abgefangen (kein unbehandelter
 * Wurf). Der Zeitgeber wird bei rechtzeitiger Antwort abgeraeumt.
 */
const mitZeitlimit = <T>(p: Promise<T>, ms: number): Promise<{ art: 'antwort'; wert: T } | { art: 'zeitlimit' }> =>
    new Promise((resolve, reject) => {
        const zeitgeber = setTimeout(() => resolve({ art: 'zeitlimit' }), ms);
        p.then(
            (wert) => { clearTimeout(zeitgeber); resolve({ art: 'antwort', wert }); },
            (fehler) => { clearTimeout(zeitgeber); reject(fehler); },
        );
    });

/**
 * „Speichern": je Zeile, die noch Stunden braucht und einen Wert hat, EIN
 * Aufruf — nacheinander (zwei Bestandteile derselben Position sperren dieselbe
 * Zeile). Wirft nie: ein Wurf von `setze` (Netz) wird ein Fehler der Zeile.
 * Jeder Fehlschlag (fehler, hinfaellig) setzt `fehlversuch` — erst danach darf
 * das Fenster ohne Stunden zu (S3).
 *
 * Zeitlimit (`zeitlimitMs`, Vorgabe 20 s) je Aufruf: laeuft es ab, zaehlt das
 * als Fehlversuch, und die uebrigen Zeilen werden gar nicht erst gesendet (die
 * Verbindung haengt). Eine spaet eintreffende Antwort aendert danach nichts
 * mehr — kam sie doch an, meldet der naechste Versuch „unveraendert" bzw.
 * „bereits eingetragen".
 */
export const speichereStunden = async (
    abfrage: StundenAbfrage,
    eingaben: Readonly<Record<string, number | null | undefined>>,
    setze: (a: { eintragId: string; stunden: number }) => Promise<StundenRpcErgebnis>,
    zeitlimitMs: number = STUNDEN_SPEICHERN_ZEITLIMIT_MS,
): Promise<StundenAbfrage> => {
    let fehlversuch = abfrage.fehlversuch;
    let haengt = false;
    const zeilen: StundenPflichtZeile[] = [];
    for (const z of abfrage.zeilen) {
        const wert = stundenZahl(eingaben[z.eintragId]);
        if (!brauchtEingabe(z) || wert === null) {
            zeilen.push(z);
            continue;
        }
        if (haengt) {
            fehlversuch = true;
            zeilen.push({ ...z, status: 'fehler', stunden: null, meldung: STUNDEN_NICHT_GESENDET_TEXT });
            continue;
        }
        let ergebnis: Pick<StundenPflichtZeile, 'status' | 'stunden' | 'meldung'>;
        try {
            const antwort = await mitZeitlimit(setze({ eintragId: z.eintragId, stunden: wert }), zeitlimitMs);
            if (antwort.art === 'zeitlimit') {
                haengt = true;
                ergebnis = { status: 'fehler', stunden: null, meldung: STUNDEN_ZEITLIMIT_TEXT };
            } else {
                ergebnis = stundenErgebnisDeuten(antwort.wert, wert);
            }
        } catch (e) {
            ergebnis = {
                status: 'fehler',
                stunden: null,
                meldung: `${STUNDEN_RPC_STATUS_TEXT.fehler} (${e instanceof Error ? e.message : String(e)})`,
            };
        }
        if (ergebnis.status === 'fehler' || ergebnis.status === 'hinfaellig') fehlversuch = true;
        zeilen.push({ ...z, ...ergebnis });
    }
    return { ...abfrage, zeilen, fehlversuch, speichert: false };
};

const ohneFensterSchluessel = (o: StundenOhneFenster): string => {
    const id = typeof o.eintragId === 'string' ? stundenEintragSchluessel(o.eintragId) : '';
    return id ? `eintrag\u0000${id}` : `${o.serviceId}\u0000${o.bestandteilLabel}`;
};

/**
 * Ein neuer Lauf des Abschlussschritts, waehrend das Pflichtfenster noch OFFEN
 * ist (zwei Ausgaben kurz nacheinander, ueberlappende Laeufe): die offene
 * Abfrage bleibt — mit Kennung, Fehlversuch, laufendem Speichern und den
 * Eingaben im Fenster. Neue, noch nicht gefragte Eintraege kommen dazu (gleiche
 * Kennung nie doppelt). Ein Eintrag, den der neue Lauf nur wegen des
 * Einmal-Schutzes ohne Fenster meldet, steht ja noch im offenen Fenster — er
 * bekommt KEINEN gelben Satz. Ohne offenes Fenster gilt die neue Abfrage.
 */
export const stundenAbfragenZusammen = (
    offen: StundenAbfrage | null | undefined,
    neu: StundenAbfrage | null | undefined,
): StundenAbfrage | null => {
    if (!offen || !stundenFensterOffen(offen)) return neu ?? null;
    if (!neu) return offen;
    const imFenster = new Set(offen.zeilen.map(z => stundenEintragSchluessel(z.eintragId)));
    const zeilen = [...offen.zeilen];
    for (const z of neu.zeilen) {
        const k = stundenEintragSchluessel(z.eintragId);
        if (imFenster.has(k)) continue;
        imFenster.add(k);
        zeilen.push(z);
    }
    const gesehen = new Set(offen.ohneFenster.map(ohneFensterSchluessel));
    const ohneFenster = [...offen.ohneFenster];
    for (const o of neu.ohneFenster) {
        const id = typeof o.eintragId === 'string' ? stundenEintragSchluessel(o.eintragId) : '';
        if (id && imFenster.has(id)) continue;
        const k = ohneFensterSchluessel(o);
        if (gesehen.has(k)) continue;
        gesehen.add(k);
        ohneFenster.push(o);
    }
    return { ...offen, zeilen, ohneFenster };
};

/**
 * Das Ergebnis eines Speicherns (gerechnet auf dem Stand bei seinem START)
 * in die Abfrage, die JETZT gilt: je Eintrag ersetzt, was gespeichert wurde;
 * Zeilen, die waehrenddessen dazugekommen sind, bleiben offen stehen.
 */
export const stundenErgebnisUebernehmen = (jetzt: StundenAbfrage, ergebnis: StundenAbfrage): StundenAbfrage => {
    const je = new Map(ergebnis.zeilen.map(z => [stundenEintragSchluessel(z.eintragId), z] as const));
    return {
        ...jetzt,
        zeilen: jetzt.zeilen.map(z => je.get(stundenEintragSchluessel(z.eintragId)) ?? z),
        fehlversuch: jetzt.fehlversuch || ergebnis.fehlversuch,
        speichert: false,
    };
};

/** „Später nachtragen" — nur nach einem Fehlversuch (S3); sonst bleibt alles, wie es ist. */
export const stundenSpaeter = (a: StundenAbfrage): StundenAbfrage =>
    a.fehlversuch && !a.speichert ? { ...a, spaeter: true } : a;

export interface StundenFensterZeile {
    eintragId: string;
    /** „Bestandteil „SiGe-Plan“ der Position „Planungsphase“" */
    titel: string;
    status: StundenZeilenStatus;
    /** Braucht noch Stunden — das Feld steht da. */
    mitFeld: boolean;
    eingabe: string;
    /** Die gelesene Stundenzahl (`parseStunden`), sonst null. */
    wert: number | null;
    /** Etwas eingegeben, das keine gueltige Stundenzahl ist. */
    ungueltig: boolean;
    meldung: string | null;
    stunden: number | null;
}

export interface StundenFensterSicht {
    zeigen: boolean;
    zeilen: StundenFensterZeile[];
    /** Jede Zeile mit Feld hat eine gueltige Stundenzahl — Pflicht (S3). */
    speichernAktiv: boolean;
    /** „Später nachtragen" erst nach einem Fehlversuch (S3). */
    spaeterErlaubt: boolean;
    speichert: boolean;
}

/**
 * Was das Fenster zeigt und erlaubt — aus der Abfrage und den Eingaben
 * (Text je Eintrag). Rein, damit die Pflichtregeln ohne DOM pruefbar sind:
 * Speichern erst mit gueltigem Wert in JEDEM Feld, Schliessen nur ueber
 * „Später nachtragen" und das erst nach einem Fehlversuch.
 */
export const stundenFensterSicht = (
    a: StundenAbfrage | null | undefined,
    eingaben: Readonly<Record<string, string | undefined>> = {},
): StundenFensterSicht => {
    if (!a || !stundenFensterOffen(a)) {
        return { zeigen: false, zeilen: [], speichernAktiv: false, spaeterErlaubt: false, speichert: false };
    }
    const zeilen: StundenFensterZeile[] = a.zeilen.map(z => {
        const mitFeld = brauchtEingabe(z);
        const eingabe = mitFeld ? String(eingaben[z.eintragId] ?? '') : '';
        const wert = mitFeld ? parseStunden(eingabe) : null;
        return {
            eintragId: z.eintragId,
            titel: `Bestandteil „${z.bestandteilLabel}“ der Position „${z.positionTitel}“`,
            status: z.status,
            mitFeld,
            eingabe,
            wert,
            ungueltig: mitFeld && eingabe.trim() !== '' && wert === null,
            meldung: z.meldung,
            stunden: z.stunden,
        };
    });
    const felder = zeilen.filter(z => z.mitFeld);
    return {
        zeigen: true,
        zeilen,
        speichernAktiv: !a.speichert && felder.length > 0 && felder.every(z => z.wert !== null),
        spaeterErlaubt: a.fehlversuch && !a.speichert,
        speichert: a.speichert,
    };
};

const fehlenSatz = (z: StundenOhneFenster): string =>
    `${STUNDEN_FEHLEN_TEXT} (Bestandteil „${z.bestandteilLabel}“ der Position „${z.positionTitel}“).`;

/**
 * Die Zeilen der Meldung zu den Stunden — erst, wenn das Fenster zu ist
 * (solange es offen ist, steht es vorn). Eine Zeile bei EINEM Bestandteil:
 * „Stunden erfasst: 8 h"; bei mehreren je Bestandteil mit Namen.
 */
export const stundenFolgeZeilen = (a: StundenAbfrage | null | undefined): FolgeZeile[] => {
    if (!a || stundenFensterOffen(a)) return [];
    const zeilen: FolgeZeile[] = [];
    const einzeln = a.zeilen.length + a.ohneFenster.length === 1;
    const zu = (z: StundenOhneFenster): string => (einzeln ? '' : ` zu „${z.bestandteilLabel}“`);
    for (const z of a.zeilen) {
        const h = z.stunden !== null ? formatStunden(z.stunden) : null;
        if (z.status === 'gespeichert') {
            zeilen.push({ art: 'stunden', text: `Stunden${zu(z)} erfasst${h ? `: ${h}` : ''}` });
        } else if (z.status === 'bereits') {
            zeilen.push({ art: 'stunden', text: `Stunden${zu(z)} bereits eingetragen${h ? ` (${h})` : ''} — nichts überschrieben` });
        } else if (z.status === 'hinfaellig') {
            zeilen.push({
                art: 'hinweis',
                text: `Stunden zu „${z.bestandteilLabel}“ der Position „${z.positionTitel}“: ${z.meldung ?? STUNDEN_RPC_STATUS_TEXT.eintrag_fehlt}`,
            });
        } else {
            zeilen.push({ art: 'stunden_fehlen', text: fehlenSatz(z) });
        }
    }
    for (const o of a.ohneFenster) zeilen.push({ art: 'stunden_fehlen', text: fehlenSatz(o) });
    return zeilen;
};

// ─────────────────────────────────────────────────────────────────────────────
// Anzeige
// ─────────────────────────────────────────────────────────────────────────────

export interface FolgeZeile {
    /**
     * `stunden`: Stunden erfasst bzw. schon eingetragen (gruen);
     * `stunden_fehlen`: der gelbe Satz „Stunden fehlen" (S3) — zaehlt fuer den
     * Rahmen wie ein Hinweis.
     */
    art: 'erledigt' | 'gebucht' | 'hinweis' | 'stunden' | 'stunden_fehlen';
    text: string;
}

/** Zeilen, die nur Hinweis sind — stehen NUR solche da, ist der Rahmen gelb. */
export const istHinweisZeile = (z: FolgeZeile): boolean => z.art === 'hinweis' || z.art === 'stunden_fehlen';

/**
 * Die Zeilen der Meldung, in fester Reihenfolge: was erledigt ist (und die
 * Stunden dazu), was gebucht wurde, was der Anwender wissen sollte. Ohne Preise.
 *   „Bestandteil ‚Vorankündigung‘ der Position ‚Planungsphase‘ erledigt (4/5)"
 *   „Stunden erfasst: 8 h"
 *   „Position ‚Planungsphase‘ gebucht — jetzt abrechenbar"
 */
export const folgeZeilen = (
    folge: AusgabeFolge | null | undefined,
    zusatz: readonly string[] = [],
    stunden: StundenAbfrage | null | undefined = null,
): FolgeZeile[] => {
    const zeilen: FolgeZeile[] = [];
    for (const e of folge?.erledigt || []) {
        const stand = (e.stand || '').trim();
        zeilen.push({
            art: 'erledigt',
            text: `Bestandteil „${e.bestandteilLabel}“ der Position „${e.positionTitel}“ erledigt${stand ? ` (${stand})` : ''}`,
        });
    }
    zeilen.push(...stundenFolgeZeilen(stunden));
    for (const g of folge?.gebucht || []) {
        zeilen.push({ art: 'gebucht', text: `Position „${g.positionTitel}“ gebucht — jetzt abrechenbar` });
    }
    const gesehen = new Set<string>();
    for (const h of [...(folge?.hinweise || []), ...zusatz]) {
        const t = (h || '').trim();
        if (!t || gesehen.has(t)) continue;
        gesehen.add(t);
        zeilen.push({ art: 'hinweis', text: t });
    }
    return zeilen;
};

/** Der Name der Quelle fuer Saetze („Vorankündigung", „A+S-Plan" …). */
export const quelleName = (quelle: BestandteilQuelle): string => BESTANDTEIL_QUELLE_LABEL[quelle] || quelle;

/**
 * Wer bucht: die angemeldete Person (`bookedBy`/`bookedById` der
 * automatischen Positionsbuchung). Der Name ueber `buildActorName` — nie
 * „Max undefined", nie ein Platzhalter; leer bleibt leer, dann setzt
 * bestandteilBuchung den Systemnamen.
 */
export const buchungsNutzer = (
    p: { id?: string | null; first_name?: string | null; last_name?: string | null } | null | undefined,
): { id: string | null; name: string } => ({
    id: p?.id ? String(p.id) : null,
    name: buildActorName(p ?? null),
});
